/*
 * altobellos-variant-media.js — ALT-PDP-BUYBOX-001 / ALT-PDP-GALLERY-001
 *
 * No-app "images change by colour". Shopify Liquid only exposes ONE image per
 * variant (`variant.featured_media`), so a native theme can't show several
 * photos per colour. Apps do it with a convention + JS; this is that, ours:
 *
 *   - The merchant tags each product image's ALT text with its colour
 *     (e.g. "black", "burgundy", "honey" — any language; see COLOUR_GROUPS).
 *   - As a FALLBACK, when alt text has no recognisable colour, the image's
 *     own filename is checked too (this catalog's naming convention already
 *     bakes the colour in, e.g. "chukkaMonserratPF-negro-lateral.jpg") —
 *     added 2026-07-17 after an audit found alt text empty catalog-wide, so
 *     the colour filter had silently never engaged on any product. Once the
 *     merchant backfills real alt text (tracked separately, product-data
 *     work, not theme code), it simply takes priority — same code path.
 *   - On the PDP, when the colour option changes, this shows only the media
 *     whose alt/filename matches the selected colour, plus media that carry
 *     NO colour keyword from either source (treated as shared, always shown)
 *     and whatever Shopify itself marks `.is-active` (the current variant's
 *     own `featured_media` — always authoritative, shown unconditionally).
 *
 * Progressive enhancement: if it can't resolve a colour, or nothing matches,
 * it shows everything — it never leaves an empty gallery. Products without
 * colour-tagged media are unaffected (all media read as "shared").
 *
 * Visibility is CSS-driven (`.alt-media-visible` class), not inline
 * `display`, and the DEFAULT state (see product-page.css) is "hidden unless
 * proven a match" rather than "shown unless proven a mismatch" — every photo
 * except the server-guaranteed-correct `.is-active` one starts hidden, so
 * nothing wrong (or everything) is ever painted even for a single frame on
 * first load or on a variant switch, no JS-timing race. A no-JS visitor sees
 * just the one guaranteed-correct photo instead of the full set — an
 * accepted trade-off for zero flash, given near-universal JS support.
 */
(function () {
  'use strict';

  // Synonym groups so a Spanish option value ("Negro") matches an English alt
  // ("black") and vice-versa. Extend freely — order/count doesn't matter.
  // "conac" (accent-stripped "coñac") sits alongside "cognac": they're
  // spelled differently (not just an accent), both need listing.
  var COLOUR_GROUPS = [
    ['black', 'negro', 'noir'],
    ['brown', 'marron', 'cafe', 'coffee', 'chocolate', 'choco'],
    ['burgundy', 'vino', 'wine', 'bordeaux', 'oxblood', 'borgona'],
    ['honey', 'miel', 'cognac', 'conac', 'caramel', 'caramelo'],
    ['tan', 'camel', 'arena'],
    ['green', 'verde', 'olive', 'oliva'],
    ['blue', 'azul', 'navy', 'marino'],
    ['grey', 'gray', 'gris'],
    ['red', 'rojo'],
    ['beige', 'cream', 'crema', 'nude', 'hueso'],
    ['white', 'blanco'],
    ['nobuck', 'nubuck', 'gamuza', 'suede'],
  ];

  // Lowercases, strips accents, and collapses every run of non-letters
  // (digits, hyphens, underscores, dots, slashes, query strings...) into a
  // single space. This makes filenames like "MONSERRAT-PFLOR-MIEL2.jpg"
  // tokenize the same way a hand-written alt tag would.
  function normalize(s) {
    return (' ' + (s || '') + ' ')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // strip accents
      .replace(/[^a-z]+/g, ' ')
      .trim();
  }

  function tokensOf(s) {
    return normalize(s).split(' ').filter(Boolean);
  }

  // Exact match, OR — for keywords 4+ letters — a suffix/prefix match, so a
  // concatenated filename with no separator between product name and colour
  // ("monserratvino.png": "monserrat" + "vino" glued together) still
  // resolves. Kept to longer keywords only: short ones like "tan"/"red"
  // would false-positive inside unrelated words ("instant", "credit").
  function tokenMatchesKeyword(token, keyword) {
    if (token === keyword) return true;
    if (keyword.length < 4 || token.length <= keyword.length) return false;
    return token.slice(-keyword.length) === keyword || token.slice(0, keyword.length) === keyword;
  }

  function anyTokenMatchesAnyKeyword(tokens, keywords) {
    return tokens.some(function (tok) {
      return keywords.some(function (k) { return tokenMatchesKeyword(tok, k); });
    });
  }

  // All keywords a colour value should match: its own words + every synonym in
  // any group it belongs to.
  function keywordsFor(value) {
    var tokens = tokensOf(value);
    var set = {};
    tokens.forEach(function (t) { set[t] = true; });
    COLOUR_GROUPS.forEach(function (group) {
      if (anyTokenMatchesAnyKeyword(tokens, group)) {
        group.forEach(function (t) { set[t] = true; });
      }
    });
    return Object.keys(set);
  }

  var ALL_COLOUR_KEYWORDS = COLOUR_GROUPS.reduce(function (all, g) { return all.concat(g); }, []);

  // true = matches the selected colour · false = confidently a DIFFERENT
  // colour · null = shared / no colour signal at all.
  //
  // Three tiers, in order:
  //  1. Matches the SELECTED colour's own keywords → true.
  //  2. Matches one of the product's OTHER *currently live* colour options
  //     (`otherKeywordSets`, read from the page, not the fixed list — a
  //     value like "Yellow" outside COLOUR_GROUPS must still count here) →
  //     false, hide it.
  //  3. Names some colour from the curated COLOUR_GROUPS list even though
  //     it ISN'T a live option any more (a leftover photo from a colour the
  //     merchant discontinued, e.g. "vino"/"miel" tagged photos on a
  //     product whose colour option no longer offers them) → false, hide
  //     it. Without this tier these orphaned photos fell into "shared" and
  //     leaked into every colour's gallery — a real, confirmed bug.
  //  Only when NONE of the above match, and the text still has no colour
  //  word from ANY source → null, genuinely shared (e.g. a size chart).
  function classify(text, selectedKeywords, otherKeywordSets) {
    var tokens = tokensOf(text);
    if (!tokens.length) return null;
    if (anyTokenMatchesAnyKeyword(tokens, selectedKeywords)) return true;
    if (otherKeywordSets.some(function (kw) { return anyTokenMatchesAnyKeyword(tokens, kw); })) return false;
    if (anyTokenMatchesAnyKeyword(tokens, ALL_COLOUR_KEYWORDS)) return false; // orphaned colour, not "shared"
    return null;
  }

  function isColourInput(input) {
    var hay = normalize(
      (input.getAttribute('name') || '') + ' ' + (input.getAttribute('data-option-name') || '')
    );
    return /color|colour|cuero|leather|piel/.test(hay);
  }

  function getSelectedColour(root) {
    var checked = root.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < checked.length; i++) {
      if (isColourInput(checked[i])) return checked[i].value;
    }
    // Fallback: the checked colour swatch label's text.
    var sw = root.querySelector('input[type="radio"]:checked + .variant-swatch-label .variant-swatch-label__text');
    return sw ? sw.textContent : '';
  }

  // Every colour value this PRODUCT actually offers (e.g. ["Yellow", "Black",
  // "Red"]) — used instead of trusting COLOUR_GROUPS alone to know what
  // counts as "a colour". COLOUR_GROUPS is a fixed curated list (English +
  // Spanish synonyms); any value outside it — "Yellow" was a real case that
  // slipped through — used to be invisible to the classifier entirely, so an
  // exact alt-text match still fell through to "can't resolve, show
  // everything". Reading the product's own option values can't miss a value
  // the catalog actually uses.
  function getAllColourValues(root) {
    var values = [];
    var seen = {};
    var radios = root.querySelectorAll('input[type="radio"]');
    Array.prototype.forEach.call(radios, function (input) {
      if (isColourInput(input) && input.value && !seen[input.value]) {
        seen[input.value] = true;
        values.push(input.value);
      }
    });
    return values;
  }

  function filenameText(url) {
    if (!url) return '';
    var clean = url.split('?')[0].split('/').pop() || '';
    return clean.replace(/\.[a-z0-9]+$/i, '');
  }

  // Combines the media's alt text with its own filename so a colour word in
  // EITHER source is enough to classify it — see file header.
  function mediaSignalText(item) {
    var altEl = item.querySelector('img, model-viewer, video, iframe, [alt]');
    var alt = altEl ? (altEl.getAttribute('alt') || '') : '';
    var img = item.querySelector('img');
    var src = img ? (img.currentSrc || img.getAttribute('src') || '') : '';
    return alt + ' ' + filenameText(src);
  }

  // The thumbnail strip (`.thumbnail-list__item`, rendered separately from
  // the main slide list in product-media-gallery.liquid) shares the same
  // `{section.id}-{media.id}` key: the main slide's `data-media-id` equals
  // the thumbnail's `data-target`. Filtering only ever touched the main
  // list — the strip stayed fully unfiltered, so a wrong-colour thumbnail
  // was still visible AND clickable (clicking it forces that photo active,
  // reintroducing the wrong colour into the main gallery). A confirmed bug.
  function findThumbnail(root, mediaId) {
    if (!mediaId) return null;
    var esc = window.CSS && CSS.escape ? CSS.escape(mediaId) : mediaId;
    return root.querySelector('.thumbnail-list__item[data-target="' + esc + '"]');
  }

  // ALT-PDP-GALLERY-004 (2026-07-18, owner-specified layout): the FIRST
  // photo is UNCONDITIONALLY the hero (own full-width row) — not just when
  // the total happens to be odd. Everything after it fills ordinary 2-up
  // rows; if THAT remainder is odd, its trailing leftover also gets a full
  // row (never a lone photo sharing a row with an empty gap). E.g. 4 photos
  // → hero, then a 2-up pair, then a trailing hero (1/2/1) — not 1/2-up×1.5.
  //
  // Making item 0 ALWAYS hero (rather than conditionally) is what finally
  // closes the first-load "small then large" gap for good: item 0 is always
  // `.is-active` (the Liquid template renders featured_media first, and
  // Dawn's own reorder-on-switch keeps it there), and product-page.css
  // defaults `.is-active` to hero-width unconditionally now — so the very
  // first CSS-only paint (before this deferred script has even run once)
  // and the final JS-computed state are ALWAYS the same rule for that
  // photo. No more "paint one thing, later prove it should've been the
  // other." No inline-style override needed either, for the same reason.
  function layoutVisible(visibleItems) {
    visibleItems.forEach(function (it) { it.classList.remove('alt-media-hero'); });
    if (!visibleItems.length) return;
    visibleItems[0].classList.add('alt-media-hero');
    var rest = visibleItems.length - 1;
    if (rest % 2 === 1) {
      visibleItems[visibleItems.length - 1].classList.add('alt-media-hero');
    }
  }

  function filterGallery(root) {
    var list = root.querySelector('.product__media-list');
    if (!list) return;
    var items = list.querySelectorAll('.product__media-item');
    if (!items.length) return;

    var colour = getSelectedColour(root);
    var keywords = colour ? keywordsFor(colour) : [];
    var otherKeywordSets = colour
      ? getAllColourValues(root)
          .filter(function (v) { return v !== colour; })
          .map(keywordsFor)
      : [];

    var results = [];
    var anyMatch = false;
    Array.prototype.forEach.call(items, function (item) {
      var verdict = keywords.length ? classify(mediaSignalText(item), keywords, otherKeywordSets) : null;
      results.push({ item: item, verdict: verdict });
      if (verdict === true) anyMatch = true;
    });

    var showAll = !colour || !anyMatch; // couldn't resolve → never empty the gallery
    var visible = [];
    results.forEach(function (r) {
      var show = showAll
        || r.item.classList.contains('is-active') // Shopify's own pick for this variant: always authoritative
        || r.verdict === true
        || r.verdict === null; // shared media, no colour word from either source
      r.item.classList.toggle('alt-media-visible', show);
      if (show) {
        visible.push(r.item);
      }

      var thumb = findThumbnail(root, r.item.getAttribute('data-media-id'));
      if (thumb) thumb.classList.toggle('alt-media-hidden', !show);
    });
    return { visible: visible };
  }

  // Grid layout is applied synchronously on every call. Variant media changes
  // are deliberately direct: no delay, opacity, transform, or animation.
  function run() {
    var root = document.querySelector('product-info') || document;
    var result = filterGallery(root);
    if (!result) return;
    layoutVisible(result.visible);
  }

  function init() {
    run();

    // Colour can change via a direct radio click (fires BEFORE Dawn's own
    // variant handler has updated the DOM — still useful, since colour
    // matching only needs the checked radio's value, not Dawn's re-render)
    // AND Dawn re-renders <product-info> asynchronously after that same
    // click (MutationObserver below, this is when `.is-active` actually
    // moves to the new photo). Both update the gallery as a direct cut.
    document.addEventListener('change', function (e) {
      if (!e.target || !e.target.matches || !e.target.matches('input[type="radio"]')) return;
      run();
    });

    var pi = document.querySelector('product-info');
    if (pi && 'MutationObserver' in window) {
      new MutationObserver(function () {
        run();
      }).observe(pi, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
